<div class="create-provider-page">

  <!-- ── Header ─────────────────────────────────────────────────────────── -->
  <header class="page-header">
    <div>
      <h1>{{ isEditMode() ? 'Edit Provider' : 'New Provider' }}</h1>
      <p>
        {{ isEditMode()
          ? 'Update this provider\'s details, coverage and status.'
          : 'Add a service provider and assign their specialties.' }}
      </p>
    </div>
    <a class="primary-teal-btn" [routerLink]="['/providers']">
      <mat-icon aria-hidden="true">arrow_back</mat-icon>
      Back
    </a>
  </header>

  <!-- ── Success ────────────────────────────────────────────────────────── -->
  @if (saved()) {
    <div class="success-card">
      <div class="success-icon-wrap">
        <mat-icon>check_circle</mat-icon>
      </div>
      <div class="success-body">
        <strong>{{ isEditMode() ? 'Provider updated successfully.' : 'Provider created successfully.' }}</strong>
        <p>
          {{ isEditMode()
            ? 'Your changes have been saved.'
            : 'You can add another provider or go back to the list.' }}
        </p>
      </div>
      <div class="success-actions">
        @if (isEditMode()) {
          <a class="primary-blue-btn" [routerLink]="['/providers', editId()]">Back to Provider</a>
        } @else {
          <button class="step-cancel-btn" (click)="addAnother()">Add Another</button>
          <a class="primary-blue-btn" [routerLink]="['/providers']">Go to Providers</a>
        }
      </div>
    </div>

  } @else {

    <!-- ── Stepper card ──────────────────────────────────────────────────── -->
    <div class="stepper-card">

      <!-- Step indicator -->
      <div class="step-indicator">
        @for (s of STEPS; track $index) {
          <div class="step-item" [class.active]="step() === $index" [class.done]="step() > $index">
            <div class="step-bubble">
              @if (step() > $index) {
                <mat-icon>check</mat-icon>
              } @else {
                {{ $index + 1 }}
              }
            </div>
            <span class="step-label">{{ s.label }}</span>
          </div>
          @if ($index < STEPS.length - 1) {
            <div class="step-connector" [class.done]="step() > $index"></div>
          }
        }
      </div>

      <!-- Step content -->
      <form [formGroup]="form" (ngSubmit)="submit()" novalidate class="step-body">

        <!-- Step 0: Basic Info -->
        @if (step() === 0) {
          <div class="step-content">
            <h2 class="step-title">Basic Info</h2>

            <div class="two-col two-col--top fields-split">

              <!-- ── Left column ──────────────────────────────────────── -->
              <div class="fields-col">

                <mat-form-field appearance="outline" floatLabel="always" class="full-width">
                  <mat-label>Provider name *</mat-label>
                  <input matInput formControlName="name" placeholder="e.g. Bright Fix Plumbing" maxlength="200" />
                  @if (form.controls.name.touched && form.controls.name.hasError('required')) {
                    <mat-error>Name is required.</mat-error>
                  }
                </mat-form-field>

                <mat-form-field appearance="outline" floatLabel="always" class="full-width">
                  <mat-label>Phone</mat-label>
                  <input matInput type="tel" formControlName="phone" placeholder="+1 (403) 000-0000" maxlength="32" />
                </mat-form-field>

                <mat-form-field appearance="outline" floatLabel="always" class="full-width">
                  <mat-label>Secondary phone</mat-label>
                  <input matInput type="tel" formControlName="secondary_phone" placeholder="+1 (403) 000-0000" maxlength="32" />
                </mat-form-field>

                <mat-form-field appearance="outline" floatLabel="always" class="full-width">
                  <mat-label>Email *</mat-label>
                  <input matInput formControlName="email" placeholder="provider@example.com" maxlength="200" />
                  @if (form.controls.email.touched && form.controls.email.hasError('required')) {
                    <mat-error>Email is required.</mat-error>
                  } @else if (form.controls.email.touched && form.controls.email.hasError('email')) {
                    <mat-error>Enter a valid email address.</mat-error>
                  }
                </mat-form-field>

                <mat-form-field appearance="outline" floatLabel="always" class="full-width">
                  <mat-label>Languages spoken *</mat-label>
                  <mat-select formControlName="languages" multiple>
                    @for (lang of LANGUAGE_OPTIONS; track lang.value) {
                      <mat-option [value]="lang.value">{{ lang.label }}</mat-option>
                    }
                  </mat-select>
                  <mat-hint>Default: English</mat-hint>
                </mat-form-field>

              </div>

              <!-- ── Right column ─────────────────────────────────────── -->
              <div class="fields-col">

                <mat-form-field appearance="outline" floatLabel="always" class="full-width">
                  <mat-label>Contact person</mat-label>
                  <input matInput formControlName="contact_name" placeholder="e.g. Michael" maxlength="200" />
                  <mat-hint>Who to call when dispatching</mat-hint>
                </mat-form-field>

                <mat-form-field appearance="outline" floatLabel="always" class="full-width">
                  <mat-label>Preferred contact</mat-label>
                  <mat-select formControlName="preferred_contact_method">
                    @for (m of CONTACT_METHODS; track m.value) {
                      <mat-option [value]="m.value">{{ m.label }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>

                <op-working-hours-input formControlName="working_hours"></op-working-hours-input>

              </div>

            </div>
          </div>
        }

        <!-- Step 1: Verification -->
        @if (step() === 1) {
          <div class="step-content">
            <h2 class="step-title">Verification</h2>
            <p class="step-subtitle">Required for home and trade services in Alberta. Set current status — you can update later.</p>

            <div class="fields fields--loose">
              <div class="toggle-card">
                <div class="toggle-card-body">
                  <div class="toggle-card-text">
                    <span>Liability insurance verified</span>
                    <small>Confirms the provider holds active liability insurance</small>
                  </div>
                  <mat-slide-toggle formControlName="insurance_verified" color="primary"></mat-slide-toggle>
                </div>
              </div>

              <div class="toggle-card">
                <div class="toggle-card-body">
                  <div class="toggle-card-text">
                    <span>WCB registered</span>
                    <small>Workers' Compensation Board (Alberta) registration confirmed</small>
                  </div>
                  <mat-slide-toggle formControlName="wcb_registered" color="primary"></mat-slide-toggle>
                </div>
              </div>

            </div>
          </div>
        }

        <!-- Step 2: Coverage & Services -->
        @if (step() === 2) {
          <div class="step-content">
            <h2 class="step-title">Coverage & Services</h2>
            <p class="step-subtitle">Define where this provider operates and which service categories they fulfil.</p>

            @if (refLoading()) {
              <div class="ref-loading">
                <mat-progress-spinner mode="indeterminate" diameter="24"></mat-progress-spinner>
                <span>Loading reference data…</span>
              </div>
            } @else {
              <div class="fields">
                <mat-form-field appearance="outline" floatLabel="always" class="full-width">
                  <mat-label>Cities served *</mat-label>
                  <mat-select formControlName="city_codes" multiple>
                    @for (city of cities(); track city.code) {
                      <mat-option [value]="city.code">{{ city.label }}</mat-option>
                    }
                  </mat-select>
                  @if (form.controls.city_codes.touched && form.controls.city_codes.hasError('required')) {
                    <mat-error>At least one city is required.</mat-error>
                  }
                </mat-form-field>

                <mat-form-field appearance="outline" floatLabel="always" class="full-width">
                  <mat-label>Assigned categories *</mat-label>
                  <mat-select formControlName="category_ids" multiple panelClass="categories-panel">
                    @for (group of categoriesByVertical(); track group[0]) {
                      <mat-optgroup [label]="verticalLabel(group[0])">
                        @for (cat of group[1]; track cat.category_id) {
                          <mat-option [value]="cat.category_id">{{ cat.name }}</mat-option>
                        }
                      </mat-optgroup>
                    }
                  </mat-select>
                  @if (form.controls.category_ids.touched && form.controls.category_ids.hasError('required')) {
                    <mat-error>At least one category is required.</mat-error>
                  }
                </mat-form-field>
              </div>
            }
          </div>
        }

        <!-- Step 3: Notes & Review -->
        @if (step() === 3) {
          <div class="step-content step-content--review">
            <div class="review-cols">

              <div class="review-left">
                <h2 class="step-title">Internal Notes</h2>
                <mat-form-field appearance="outline" floatLabel="always" class="full-width notes-field">
                  <mat-label>Operator notes</mat-label>
                  <textarea
                    matInput
                    formControlName="internal_notes"
                    rows="5"
                    maxlength="2000"
                    placeholder="e.g. Doesn't take weekend calls. Prefers e-transfer."
                  ></textarea>
                  <mat-hint>Not visible to customers</mat-hint>
                </mat-form-field>

                <mat-form-field appearance="outline" floatLabel="always" class="full-width notes-field">
                  <mat-label>Admin notes</mat-label>
                  <textarea
                    matInput
                    formControlName="admin_notes"
                    rows="5"
                    maxlength="2000"
                    placeholder="e.g. Contract signed 2025-01. Follow up on insurance renewal."
                  ></textarea>
                  <mat-hint>Admin only · Not visible to operators or customers</mat-hint>
                </mat-form-field>

                <div class="selectors-row">
                  @if (isSuperAdmin()) {
                    <mat-form-field appearance="outline" floatLabel="always" class="selector-half">
                      <mat-label>Trust status</mat-label>
                      <mat-select formControlName="trust_status">
                        <mat-option value="">— Not set —</mat-option>
                        @for (opt of TRUST_STATUS_OPTIONS; track opt.value) {
                          <mat-option [value]="opt.value">{{ opt.icon }} {{ opt.label }}</mat-option>
                        }
                      </mat-select>
                      <mat-hint>Admin only · Internal trust rating</mat-hint>
                    </mat-form-field>
                  }
                </div>
              </div>

              <div class="review-right">
                <h2 class="step-title">Review</h2>
                <div class="review-list">
                  @for (item of reviewSummary(); track item.label) {
                    <div class="review-row">
                      <span class="review-label">{{ item.label }}</span>
                      <span class="review-value">{{ item.value }}</span>
                    </div>
                  }
                </div>
              </div>

            </div>

            @if (error()) {
              <div class="error-banner">
                <mat-icon>error_outline</mat-icon>
                {{ error() }}
              </div>
            }
          </div>
        }

        <!-- Step actions -->
        <div class="step-actions">
          <div class="step-actions-left">
            <button
              type="button"
              class="step-cancel-btn"
              [disabled]="step() === 0"
              (click)="prev()"
            >
              <mat-icon>arrow_back</mat-icon>
              Back
            </button>
            <a class="step-cancel-btn" [routerLink]="['/providers']">Cancel</a>
          </div>

          <div class="step-actions-right">
            @if (!isLastStep()) {
              <button
                type="button"
                class="primary-blue-btn"
                (click)="next()"
              >
                Next
                <mat-icon>arrow_forward</mat-icon>
              </button>
            } @else {
              <div class="activate-check">
                <mat-checkbox formControlName="activate_provider" color="primary">
                  {{ isEditMode() ? 'Provider active?' : 'Activate Provider?' }}
                </mat-checkbox>
                <mat-icon
                  class="info-icon"
                  matTooltip="This provider will be automatically activated and available for customers."
                  matTooltipPosition="above"
                >info_outline</mat-icon>
              </div>
              <button
                type="submit"
                class="primary-green-btn"
                [disabled]="formInvalid() || saving()"
              >
                @if (saving()) {
                  <mat-progress-spinner mode="indeterminate" diameter="18"></mat-progress-spinner>
                  Saving…
                } @else {
                  {{ isEditMode() ? 'Save Changes' : 'Create Provider' }}
                }
              </button>
            }
          </div>
        </div>

      </form>
    </div>
  }

</div>
